import React from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import {
  StatCard,
  CardHeading,
  CardSecondaryText,
  cardStatIconSx,
  formatNumber,
} from "./MediaKitComponents";
import {
  MediaKitMetricsWindowCaption,
  type MediaKitContentFormat,
} from "./MediaKitMetricsWindowCaption";

const iconSx = cardStatIconSx(30);

/** Engagement % from catalog totals (interacciones / views). */
export function catalogEngagementPercentFromConsumption(
  c: { totalViews?: number; totalInteractions?: number } | undefined,
): number | undefined {
  if (c == null) return undefined;
  const v = c.totalViews;
  const i = c.totalInteractions;
  if (v == null || v <= 0 || i == null || i < 0) return undefined;
  return Math.round((i / v) * 10000) / 100;
}

function CatalogEngagementStatCell({ rate }: { rate: number }) {
  const { t } = useTranslation("mediaKit");
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <StatCard>
        <ThumbUpIcon sx={iconSx} />
        <CardHeading variant="h5">{rate.toFixed(2)}%</CardHeading>
        <CardSecondaryText variant="body2">
          {t("metrics.engagementRate")}
        </CardSecondaryText>
      </StatCard>
    </Grid>
  );
}

export type YoutubeCatalogConsumption = {
  totalViews?: number;
  avgViewsPerContent?: number;
  totalInteractions?: number;
  avgInteractions?: number;
  contentCount?: number;
  inProgressCount?: number;
};

export type PodscopeCatalogConsumption = {
  totalViews?: number;
  avgViews?: number;
  totalInteractions?: number;
  avgInteractions?: number;
  inProgressCount?: number;
};

export const MediaKitYoutubeCatalogConsumptionGrid: React.FC<{
  consumption?: YoutubeCatalogConsumption | undefined;
  engagementRate?: number;
  contentCount?: number;
  contentCountLabel?: string;
  contentCountIcon?: "video" | "short";
  totalViewsLabel?: string;
}> = ({
  consumption,
  engagementRate,
  contentCount,
  contentCountLabel,
  contentCountIcon = "video",
  totalViewsLabel,
}) => {
  const { t } = useTranslation("mediaKit");
  const { totalViews, avgViewsPerContent, totalInteractions, avgInteractions } =
    consumption ?? {};
  const hasConsumption =
    totalViews != null ||
    avgViewsPerContent != null ||
    totalInteractions != null ||
    avgInteractions != null;
  const showEngagement = engagementRate != null;
  if (!hasConsumption && !showEngagement && contentCount == null) return null;
  return (
    <Grid container spacing={2}>
      {contentCount != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            {contentCountIcon === "short" ? (
              <SlideshowIcon sx={iconSx} />
            ) : (
              <OndemandVideoIcon sx={iconSx} />
            )}
            <CardHeading variant="h5">{contentCount}</CardHeading>
            <CardSecondaryText variant="body2">
              {contentCountLabel ?? t("metrics.videos")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {totalViews != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <VisibilityIcon sx={iconSx} />
            <CardHeading variant="h5">{formatNumber(totalViews)}</CardHeading>
            <CardSecondaryText variant="body2">
              {totalViewsLabel ?? t("metrics.totalViews")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {avgViewsPerContent != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <EqualizerIcon sx={iconSx} />
            <CardHeading variant="h5">
              {formatNumber(avgViewsPerContent)}
            </CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.avgViews")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {totalInteractions != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <TouchAppIcon sx={iconSx} />
            <CardHeading variant="h5">
              {formatNumber(totalInteractions)}
            </CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.totalInteractions")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {avgInteractions != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <TouchAppIcon sx={iconSx} />
            <CardHeading variant="h5">
              {formatNumber(avgInteractions)}
            </CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.avgInteractions")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {showEngagement && engagementRate != null && (
        <CatalogEngagementStatCell rate={engagementRate} />
      )}
    </Grid>
  );
};

export const MediaKitPodscopeCatalogConsumptionGrid: React.FC<{
  consumption?: PodscopeCatalogConsumption | undefined;
  engagementRate?: number;
  contentCount?: number;
}> = ({ consumption, engagementRate, contentCount }) => {
  const { t } = useTranslation("mediaKit");
  const { totalViews, avgViews, totalInteractions, avgInteractions } =
    consumption ?? {};
  const hasConsumption =
    totalViews != null ||
    avgViews != null ||
    totalInteractions != null ||
    avgInteractions != null;
  const showEngagement = engagementRate != null;
  if (!hasConsumption && !showEngagement && contentCount == null) return null;
  return (
    <Grid container spacing={2}>
      {contentCount != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <OndemandVideoIcon sx={iconSx} />
            <CardHeading variant="h5">{contentCount}</CardHeading>
            <CardSecondaryText variant="body2">{t("metrics.videos")}</CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {totalViews != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <VisibilityIcon sx={iconSx} />
            <CardHeading variant="h5">{formatNumber(totalViews)}</CardHeading>
            <CardSecondaryText variant="body2">{t("metrics.totalViews")}</CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {avgViews != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <VisibilityIcon sx={iconSx} />
            <CardHeading variant="h5">{formatNumber(avgViews)}</CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.avgViews")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {totalInteractions != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <TouchAppIcon sx={iconSx} />
            <CardHeading variant="h5">
              {formatNumber(totalInteractions)}
            </CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.totalInteractions")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {avgInteractions != null && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard>
            <TouchAppIcon sx={iconSx} />
            <CardHeading variant="h5">
              {formatNumber(avgInteractions)}
            </CardHeading>
            <CardSecondaryText variant="body2">
              {t("metrics.avgInteractions")}
            </CardSecondaryText>
          </StatCard>
        </Grid>
      )}
      {showEngagement && engagementRate != null && (
        <CatalogEngagementStatCell rate={engagementRate} />
      )}
    </Grid>
  );
};

export const MediaKitConsumptionSubsection: React.FC<{
  title: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
  /** Days-after-publish window caption (only renders when the media kit has it on). */
  metricsWindow?: { format: MediaKitContentFormat; inProgressCount?: number };
}> = ({ title, headerAction, children, metricsWindow }) => (
  <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
      }}
    >
      <CardHeading variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </CardHeading>
      {headerAction}
    </Box>
    {children}
    {metricsWindow && (
      <MediaKitMetricsWindowCaption
        format={metricsWindow.format}
        inProgressCount={metricsWindow.inProgressCount}
      />
    )}
  </Box>
);
